import { count } from "../../lib/format";
import { money } from "../../lib/tariff";
import type { PublicStats } from "../../lib/types";
import { MicroLabel, SectionTitle } from "../ui/Text";
import { Figure } from "./Figure";
import { PanelLink } from "./PanelLink";

/**
 * Public charging in the window: what it took, and where orders fell out between
 * being placed and delivering energy.
 *
 * One figure per currency and never a total across them — a riel and a cent are
 * both "minor units" and nothing else alike (`publicStatsRepository.ts`).
 */
export function MoneyPanel({
  stats,
  label,
}: { readonly stats: PublicStats; readonly label: string }) {
  const { funnel } = stats;
  const steps = [
    { label: "Placed", value: funnel.created },
    { label: "Paid", value: funnel.paid },
    { label: "Started", value: funnel.started },
    { label: "Delivered", value: funnel.delivered },
  ];

  return (
    <section aria-labelledby="money-title" className="flex flex-col gap-4">
      <SectionTitle action={<PanelLink to="/public-orders">Public charging</PanelLink>}>
        <span id="money-title">Public charging · {label}</span>
      </SectionTitle>

      <dl className="m-0 grid grid-cols-2 gap-4 md:grid-cols-3">
        {stats.revenue.length === 0 ? (
          <Figure label="Net revenue" value="—" sub="No orders placed in this window" />
        ) : (
          stats.revenue.map((row) => (
            <Figure
              key={row.currency}
              label={`Net ${row.currency}`}
              value={money(row.netMinor, row.currency)}
              sub={
                row.refundedMinor === "0"
                  ? `${count(row.orders)} paid ${row.orders === 1 ? "order" : "orders"}`
                  : `${money(row.grossMinor, row.currency)} taken, ${money(row.refundedMinor, row.currency)} refunded`
              }
            />
          ))
        )}
        <Figure label="Declined payments" value={count(stats.declinedPayments)} />
      </dl>

      <div className="flex flex-col gap-2">
        <MicroLabel>Orders</MicroLabel>
        {/* A funnel read left to right, each step a share of the first, so the
            step where drivers drop out is the one whose bar falls away. */}
        <ol className="m-0 grid list-none grid-cols-4 gap-3 p-0">
          {steps.map((step) => (
            <li key={step.label} className="flex flex-col gap-1">
              <span className="h-1.5 bg-paper-sunken" aria-hidden="true">
                <span
                  className="block h-full bg-ink"
                  style={{
                    width: `${funnel.created === 0 ? 0 : (step.value / funnel.created) * 100}%`,
                  }}
                />
              </span>
              <span className="text-data text-ink-muted">{step.label}</span>
              <span className="text-body font-semibold text-ink tabular-nums">
                {count(step.value)}
              </span>
            </li>
          ))}
        </ol>
      </div>

      {stats.paymentOptions.length === 0 ? null : (
        <p className="m-0 text-data text-ink-faint">
          Paid by{" "}
          {stats.paymentOptions.map((row) => `${row.option} ${count(row.count)}`).join(" · ")}
        </p>
      )}
    </section>
  );
}
